import { BeeswaxReportProperties } from '@/Enums/Report/BeeswaxReportProperties.enum';
import type ViewType from '@/Enums/ReportingDashboard/ViewType.enum';
import type { IState } from '@/Interfaces/Slices/ReportingDashboard/Simplifi/State.interface';
import { defaultTotals, initialState } from '@/constants/reportingDashboard/simplifi';
import { DEFAULT_LOADING_REQUEST_ID } from '@/constants/slice';
import { fulfilledReportCaseHandler } from '@/helpers/reportingDashboard/slice';
import { fulfilledCaseHandler, pendingCaseHandler, rejectedCaseHandler } from '@/store/asyncThunkHandlers';
import { createSlice } from '@reduxjs/toolkit';
import { boundaryMap, getCampaignReport, getTotalReport, reportMap } from './simplifiThunks';

export const reportingDashboardSimplifi = createSlice({
  name: 'reportingDashboardSimplifi',
  initialState,
  reducers: {
    resetSimplifiReportsLoadingState: state => {
      Object.entries(state).forEach(([key]) => {
        if (key.endsWith('RequestId')) {
          (state[key as keyof IState] as string) = DEFAULT_LOADING_REQUEST_ID;
        }
      });
      state.errorCardMessage = '';
      // Clear the "generating data" banner — fresh filters mean the BE
      // will re-evaluate cache hits and re-flag dataIsRefreshing if any
      // report's snapshot is still missing.
      state.dataIsRefreshing = false;
    },
    // Optimistically clears the "generating data" banner ahead of a poll
    // re-fetch (#4559), without touching requestIds/errorCardMessage the
    // way a full reset would. The fulfilled-action addMatcher below re-flags
    // it true if any re-fetched report still comes back still-refreshing.
    clearSimplifiDataIsRefreshing: state => {
      state.dataIsRefreshing = false;
    },
    markSimplifiDataIsRefreshing: state => {
      state.dataIsRefreshing = true;
    },
    // GeoFence Delivery / Attribution toggle. Mirrors the OmniChannel
    // ViewType pattern but lives on its own slice key so the two tenants
    // don't share state. Loaders gate on this when GeoFence is the active
    // view; the rest of the dashboard ignores it.
    setSimplifiViewType: (state, { payload }: { payload: ViewType }) => {
      state.viewType = payload;
    },
  },
  extraReducers: builder => {
    reportMap.forEach(([key, thunk]) => {
      builder
        .addCase(thunk.pending, pendingCaseHandler(key, []))
        .addCase(thunk.rejected, rejectedCaseHandler(key, []))
        .addCase(thunk.fulfilled, fulfilledReportCaseHandler(key));
    });

    boundaryMap.forEach(([key, thunk]) => {
      builder
        .addCase(thunk.pending, pendingCaseHandler(key))
        .addCase(thunk.rejected, rejectedCaseHandler(key))
        .addCase(thunk.fulfilled, fulfilledCaseHandler(key));
    });

    builder
      .addCase(getTotalReport.pending, pendingCaseHandler('totals'))
      .addCase(getTotalReport.rejected, (state, action) => {
        if (state.totalsRequestId === action.meta.requestId) {
          state.errorCardMessage = 'There seems to be a momentary issue loading your data. Please retry.';
          state.totalsRequestId = null;
        }
      })
      .addCase(getTotalReport.fulfilled, (state, action) => {
        if (state.totalsRequestId === action.meta.requestId) {
          const { totals } = action.payload;

          state.totalsRequestId = null;
          state.totals = totals ?? defaultTotals;
          // Only show the "couldn't find any data" error card when the BE
          // returned no totals object at all (genuinely missing). A zero
          // impressions count for a real date range is legitimate (e.g. no
          // delivery yet this month) and the dashboard's other panels —
          // Geo-Fence, geo, day-of-week — may still have data the user
          // wants to see. Empty panels speak for themselves; the error
          // card is reserved for "we hit a real problem" cases.
          if (!totals) {
            state.errorCardMessage = "We couldn't find any data. Please adjust your filters and try again.";
          } else if (!state.totals[BeeswaxReportProperties.REACH] || !state.totals[BeeswaxReportProperties.FREQUENCY]) {
            // Simplifi returns Reach/Frequency = 0 when the snapshot pre-fetch
            // cache hasn't populated them yet. Convert those "still computing"
            // 0s to null so the UI can render "—" with a tooltip.
            state.totals = {
              ...state.totals,
              [BeeswaxReportProperties.REACH]: null,
              [BeeswaxReportProperties.FREQUENCY]: null,
            };
          }
        }
      })
      .addCase(getCampaignReport.pending, pendingCaseHandler('campaignReport'))
      .addCase(getCampaignReport.rejected, rejectedCaseHandler('campaignReport', []))
      .addCase(getCampaignReport.fulfilled, (state, action) => {
        const { report, filters } = action.payload;
        if (state.campaignReportRequestId === action.meta.requestId) {
          state.campaignReportRequestId = null;
          state.campaignReport = report;

          if (filters?.campaigns?.length === 0) {
            state.campaigns = report?.length
              ? report
                  .filter(campaign => campaign[BeeswaxReportProperties.IMPRESSIONS] > 0)
                  .map(campaign => ({
                    value: campaign[BeeswaxReportProperties.CAMPAIGN_ID],
                    label: campaign[BeeswaxReportProperties.CAMPAIGN_NAME],
                  }))
                  .sort((a, b) => a.label.localeCompare(b.label))
              : [];
          }
        }
      });

    // Any Simplifi report fulfilled with dataIsRefreshing=true raises the
    // dashboard's "generating data" banner. We OR the flag across every
    // report so a single missing snapshot is enough to trigger the message.
    // Cleared by resetSimplifiReportsLoadingState on filter apply.
    builder.addMatcher(
      action =>
        typeof action.type === 'string' &&
        action.type.startsWith('reportingDashboardSimplifi/') &&
        action.type.endsWith('/fulfilled'),
      (state, action) => {
        if (action.payload?.dataIsRefreshing) {
          state.dataIsRefreshing = true;
        }
      },
    );
  },
});

export const {
  resetSimplifiReportsLoadingState,
  clearSimplifiDataIsRefreshing,
  markSimplifiDataIsRefreshing,
  setSimplifiViewType,
} = reportingDashboardSimplifi.actions;

export default reportingDashboardSimplifi.reducer;
